import { useFieldArray, UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import DeleteIcon from "../../icons/delete.icon";
import { FancySelect, LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";
import { useTranslation } from "../../core/translation.core";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import paycore from "../../core/pay.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { EditorSection, Title } from "../reuseables/editor.comp";
import PlusIcon from "../../icons/plus.icon";
import { ManualOrderFormData } from "../../types/order";

export const PaymentPortionArr = ["fullPayment", "partPayment", "deposit", "balance"];

export default function ManuaOrderPaymentsComp({ methods }: { methods: UseFormReturn<ManualOrderFormData, any> }) {
  const { t, msg } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "payments",
  });

  if (fields.length === 0) {
    return (
      <EditorSection>
        <Title>
          <div className="flex flex-row xl:flex-col gap-4 items-center xl:items-start">
            {t(msg.payment.payments)}
            <div className="flex flex-col md:flex-row gap-4">
              <AddBtn append={append} />
            </div>
          </div>
        </Title>
        <div></div>
      </EditorSection>
    );
  }

  return (
    <>
      {fields.map((field, index) => (
        <PaymentItem
          key={field.id}
          index={index}
          methods={methods}
          append={append}
          remove={remove}
          showAddBtn={index === fields.length - 1}
        />
      ))}
    </>
  );
}

function PaymentItem({
  index,
  methods,
  append,
  remove,
  showAddBtn,
}: {
  index: number;
  methods: UseFormReturn<ManualOrderFormData, any>;
  append: UseFieldArrayAppend<ManualOrderFormData, "payments">;
  remove: UseFieldArrayRemove;
  showAddBtn: boolean;
}) {
  const { t, msg } = useTranslation();

  const getPaymentPortionLabel = (portion: string) => {
    switch (portion) {
      case "fullPayment":
        return t(msg.paymentPortion.fullPayment);
      case "partPayment":
        return t(msg.paymentPortion.partPayment);
      case "balance":
        return t(msg.paymentPortion.balance);
      case "deposit":
        return t(msg.paymentPortion.deposit);
      default:
        return "";
    }
  };

  return (
    <EditorSection>
      <Title>
        <div className="flex flex-row xl:flex-col gap-4 items-center xl:items-start">
          <span>{t(msg.payment.payments) + " #" + (index + 1)}</span>
          <div className="flex flex-row gap-4">
            {showAddBtn && <AddBtn append={append} />}
            <button type="button" onClick={() => remove(index)} className="bg-red-700 text-white px-2 py-2 rounded-full">
              <DeleteIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Title>
      <div>
        <label className="flex flex-row gap-4">{t(msg.payment.payments)}</label>
        <FancySelect
          register={methods.register}
          fieldName={`payments.${index}.paymentPortion`}
          className="w-full"
          options={PaymentPortionArr.map((type) => ({
            label: getPaymentPortionLabel(type),
            value: type,
          }))}
        />

        <PayType methods={methods} index={index} />
        <LabelAndInput
          type="number"
          register={methods.register}
          fieldName={`payments.${index}.amountReceived`}
          topLabelCss="w-full"
          labelCss="mt-2"
          registerOptions={{ min: 0 }}
          errors={methods.formState.errors}
          label={t(msg.paymentPortion.amountRecieved)}
        />
      </div>
    </EditorSection>
  );
}

export const PayType = ({ methods, index }: { methods: UseFormReturn<any, any>; index?: number }) => {
  const { t, msg } = useTranslation();
  const { locale, isReady } = useRouter();

  const {
    data: paySettings,
    isPending,
    isError,
    failureReason,
  } = useQuery({
    queryKey: ["paySettings", locale],
    queryFn: paycore.list,
    enabled: isReady,
  });

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!paySettings) return null;

  const getLocalizedName = (paySetting: any) => {
    return paySetting.name?.[locale as keyof typeof paySetting.name] || t(paySetting.name) || "Unknown Payment";
  };

  const paymentOptions = [
    { label: "", value: "" }, // Default empty option
    ...paySettings.map((paySetting) => ({
      label: getLocalizedName(paySetting),
      value: paySetting.payType,
    })),
  ];

  return (
    <LabelAndSelect
      methods={methods}
      label={t(msg.pay.payType)}
      fieldName={index === undefined ? "paymentName" : (`payments.${index}.payType` as any)}
      options={paymentOptions}
      registerOptions={{
        required: "Please select a payment type",
      }}
    />
  );
};

function AddBtn({ append }: { append: UseFieldArrayAppend<any, "payments"> }) {
  return (
    <button
      id="add-payment"
      type="button"
      onClick={() => append({ paymentPortion: "", amountReceived: 0 })}
      className="bg-blue-600 text-white px-2 py-2 rounded-full"
    >
      <PlusIcon className="w-4 h-4" />
    </button>
  );
}
